import React, { useState, useRef } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { buildDocumentData } from "../utils/documentTemplates";
import EndorsementLetter from "./EndorsementLetter";
import COE from "./COE";

const PREVIEW_APPLICANT = {
  firstName: "Juan", middleName: "D.", lastName: "dela Cruz",
  gender: "Male", civilStatus: "Single",
  education: { college: { yearGraduated: "2020", monthGraduated: "4" } } // month is 1-12, as the application form saves it
};
// Sample values like the ones the admin page rolls and saves per applicant
const PREVIEW_DURATION = { years: 2, months: 5 };
const PREVIEW_CERT_NO = `${new Date().getFullYear()}-COE-001`;

const FIELD_GROUPS = [
  {
    label: "Company Information",
    fields: [
      { key: "company",    label: "Company Name",        placeholder: "e.g., Pureflow Laundry Station", full: true },
      { key: "manager",    label: "Manager / Signatory", placeholder: "e.g., Gerald Durano" },
      { key: "dtiCert",   label: "DTI Certificate No.",  placeholder: "e.g., 7405689" },
      { key: "issueCity", label: "Issue City",           placeholder: "e.g., Poblacion, Asturias, Cebu", full: true },
    ]
  },
  {
    label: "Job Details",
    fields: [
      { key: "role",           label: "Job Role (short)",     placeholder: "e.g., Laundry Worker" },
      { key: "longRole",       label: "Job Role (full title)", placeholder: "e.g., Laundry Worker / Machine Operator", full: true },
      { key: "endorseAs",      label: "Endorse As",           placeholder: "e.g., Laundry Worker" },
      { key: "industry",       label: "Industry",             placeholder: "e.g., laundry industry" },
      { key: "expertise",      label: "Area of Expertise",    placeholder: "e.g., laundry operations", full: true },
      { key: "employerStatus", label: "Employer Status",      type: "select",
        options: [["current","Current Employee"],["former","Former Employee"]] },
    ]
  },
  {
    label: "Duties",
    fields: [
      { key: "duties",    label: "Duties — Endorsement Letter", placeholder: "e.g., washing, drying, ironing...", type: "textarea", full: true },
      { key: "coeDuties", label: "Duties — COE (blank = same as above)", placeholder: "Leave blank to copy from above", type: "textarea", full: true },
    ]
  }
];

function ImageUploadField({ label, hint, value, uploading, onUpload, onClear }) {
  const inputRef = useRef();
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#4a3f35", display: "block", marginBottom: 6 }}>
        {label}
      </label>
      {value ? (
        <div style={{ position: "relative", border: "1px solid #e0d8cc", borderRadius: 6, overflow: "hidden", background: "#f5f0e8" }}>
          <img src={value} alt={label} style={{ width: "100%", maxHeight: 120, objectFit: "contain", display: "block", padding: 8 }} />
          <div style={{ display: "flex", gap: 6, padding: "6px 8px", borderTop: "1px solid #e0d8cc", background: "white" }}>
            <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: "3px 10px" }}
              onClick={() => inputRef.current.click()} disabled={uploading}>
              <i className="ti ti-replace"></i> Replace
            </button>
            <button type="button" className="btn btn-danger" style={{ fontSize: 11, padding: "3px 10px" }}
              onClick={onClear}>
              <i className="ti ti-trash"></i> Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current.click()}
          style={{
            border: "2px dashed #c8b89a", borderRadius: 6, padding: "20px 16px",
            textAlign: "center", cursor: "pointer", background: "#fdf9f3",
            color: "#8a7868", fontSize: 13
          }}
        >
          {uploading
            ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></span> Uploading...</>
            : <><i className="ti ti-upload" style={{ fontSize: 20, display: "block", marginBottom: 4 }}></i>{hint}</>
          }
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
    </div>
  );
}

export default function DocumentTemplateEditor({ template, onSave, onCancel }) {
  const [tpl, setTpl] = useState({ ...template });
  const [previewDoc, setPreviewDoc] = useState("endorsement");
  const [uploading, setUploading] = useState({ logo: false, letterhead: false, signature: false });

  const upd = (key, val) => setTpl((p) => ({ ...p, [key]: val }));

  const upload = async (type, file) => {
    setUploading((u) => ({ ...u, [type]: true }));
    try {
      const path = `templates/${type}s/${Date.now()}_${file.name}`;
      const snap = await uploadBytes(ref(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      upd(type === "logo" ? "logoUrl" : "letterheadUrl", url);
    } catch (e) { console.error("Upload failed:", e); }
    setUploading((u) => ({ ...u, [type]: false }));
  };

  const previewTemplate = { ...tpl, coeDuties: tpl.coeDuties || tpl.duties };
  const docData = tpl.company ? buildDocumentData(PREVIEW_APPLICANT, previewTemplate, new Date(), PREVIEW_DURATION, PREVIEW_CERT_NO) : null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(13,10,8,0.75)", display: "flex", flexDirection: "column" }}>

      {/* Header bar */}
      <div style={{
        background: "#1a1410", color: "#efc04d", padding: "14px 24px", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "2px solid #c8881b"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <i className="ti ti-file-certificate" style={{ fontSize: 20 }}></i>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700 }}>Document Template Editor</div>
            <div style={{ fontSize: 12, color: "#ddd0bf", marginTop: 2 }}>
              {tpl.company || "No company set"} — live preview updates as you type
            </div>
          </div>
        </div>
        <button onClick={onCancel} style={{ background: "none", border: "none", color: "#ddd0bf", fontSize: 22, cursor: "pointer" }}>
          <i className="ti ti-x"></i>
        </button>
      </div>

      {/* Body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* LEFT — form fields */}
        <div style={{ width: 380, flexShrink: 0, background: "#faf6ef", overflowY: "auto", padding: "20px", borderRight: "1px solid #e0d8cc" }}>

          {/* Letterhead & Logo section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em",
              color: "#c8881b", marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid #e0d8cc"
            }}>
              Letterhead &amp; Logo
            </div>

            <ImageUploadField
              label="Letterhead Image"
              hint="Upload a full-width letterhead banner (PNG/JPG). Replaces the text header when present."
              value={tpl.letterheadUrl}
              uploading={uploading.letterhead}
              onUpload={(file) => upload("letterhead", file)}
              onClear={() => upd("letterheadUrl", "")}
            />

            <ImageUploadField
              label="Company Logo"
              hint="Upload company logo (PNG/JPG). Shown beside company name when no letterhead is set."
              value={tpl.logoUrl}
              uploading={uploading.logo}
              onUpload={(file) => upload("logo", file)}
              onClear={() => upd("logoUrl", "")}
            />

            <ImageUploadField
              label="Signature Image"
              hint="Upload manager signature (PNG with transparent background recommended). Appears above the manager name."
              value={tpl.signatureUrl}
              uploading={uploading.signature}
              onUpload={(file) => upload("signature", file)}
              onClear={() => upd("signatureUrl", "")}
            />

            <div style={{ background: "#fdf3dc", border: "1px solid #e8c97a", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#7a5c1e" }}>
              <strong>Tip:</strong> If a <em>Letterhead</em> is set, it appears at the top of the document as a full banner (like pre-printed stationery).
              If only a <em>Logo</em> is set, it appears beside the company name in the text header.
            </div>
          </div>

          {/* Other field groups */}
          {FIELD_GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em",
                color: "#c8881b", marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid #e0d8cc"
              }}>
                {group.label}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {group.fields.map((f) => (
                  <div key={f.key} style={{ gridColumn: f.full ? "1 / -1" : undefined }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#4a3f35", display: "block", marginBottom: 4 }}>{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea rows={3} className="form-control" style={{ fontSize: 13, resize: "vertical" }}
                        value={tpl[f.key] || ""} placeholder={f.placeholder}
                        onChange={(e) => upd(f.key, e.target.value)} />
                    ) : f.type === "select" ? (
                      <select className="form-control" style={{ fontSize: 13 }}
                        value={tpl[f.key] || "current"} onChange={(e) => upd(f.key, e.target.value)}>
                        {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    ) : (
                      <input className="form-control" style={{ fontSize: 13 }}
                        value={tpl[f.key] || ""} placeholder={f.placeholder}
                        onChange={(e) => upd(f.key, e.target.value)} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 8, paddingTop: 8, borderTop: "1px solid #e0d8cc" }}>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => onSave(tpl)}>
              <i className="ti ti-check"></i> Save Template
            </button>
            <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          </div>
        </div>

        {/* RIGHT — live document preview */}
        <div style={{ flex: 1, overflowY: "auto", background: "#c8c0b8", display: "flex", flexDirection: "column" }}>
          <div style={{ background: "#3a3028", padding: "8px 20px", display: "flex", gap: 4, flexShrink: 0, borderBottom: "1px solid #5a4e44" }}>
            {[["endorsement","Endorsement Letter","ti-letter-case"],["coe","Certificate of Employment","ti-award"]].map(([key, label, icon]) => (
              <button key={key} onClick={() => setPreviewDoc(key)} style={{
                padding: "6px 16px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 13,
                background: previewDoc === key ? "#c8881b" : "rgba(255,255,255,0.1)",
                color: previewDoc === key ? "white" : "#ddd0bf", fontWeight: previewDoc === key ? 600 : 400
              }}>
                <i className={`ti ${icon}`}></i> {label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: "#8a7868", alignSelf: "center" }}>Sample: Juan D. dela Cruz</span>
          </div>

          <div style={{ flex: 1, padding: "32px 24px", display: "flex", justifyContent: "center" }}>
            {!tpl.company ? (
              <div style={{ color: "#8a7868", textAlign: "center", marginTop: 80 }}>
                <i className="ti ti-file-off" style={{ fontSize: 48 }}></i>
                <div style={{ marginTop: 12 }}>Fill in Company Name to see the preview</div>
              </div>
            ) : docData ? (
              <div style={{
                background: "white", width: "100%", maxWidth: 816, minHeight: 1056,
                padding: "72px 96px", boxShadow: "0 4px 24px rgba(0,0,0,0.3)", boxSizing: "border-box"
              }}>
                {previewDoc === "endorsement" ? <EndorsementLetter data={docData} /> : <COE data={docData} />}
              </div>
            ) : (
              <div style={{ color: "#8a7868", textAlign: "center", marginTop: 80 }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 48 }}></i>
                <div style={{ marginTop: 12 }}>Could not generate preview</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
